import { useSyncExternalStore } from 'react';

/**
 * The agents whose setup the user has started with Clu in this browser: the
 * first message they send in an agent's workspace, from the dock or from the
 * workspace's own composer. The workspace then swaps its guided "What should
 * this agent take care of?" screen for the panel, read only while Clu sets
 * the agent up, the way Gumloop's panel appears with the first message. The
 * server's `setupCompletedAt` unlocks editing later.
 *
 * Kept in localStorage so a reload keeps the panel, with an in-memory mirror
 * for browsers where storage is blocked. Every storage call is guarded.
 */

const STORAGE_KEY = 'cleanlist:agents:setup-started:v1';
const CHANGE_EVENT = 'cleanlist:agent-setup-started';

const startedThisPage = new Set<string>();

function readStored(): Record<string, true> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, true>)
      : {};
  } catch {
    return {};
  }
}

export function isAgentSetupStarted(agentId: string): boolean {
  return startedThisPage.has(agentId) || readStored()[agentId] === true;
}

export function markAgentSetupStarted(agentId: string): void {
  if (!agentId || isAgentSetupStarted(agentId)) return;
  startedThisPage.add(agentId);
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readStored(), [agentId]: true })
    );
  } catch {
    // Storage is blocked: the in-memory mirror carries it for this page.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** True once the user has sent their first message about this agent. */
export function useAgentSetupStarted(agentId: string | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => (agentId ? isAgentSetupStarted(agentId) : false),
    () => false
  );
}
